import React, { useEffect } from 'react'
import { X, Heart, ExternalLink } from 'lucide-react'
import { useFavorites } from '../contexts/FavoritesContext'
import { favoritePrice, favoritesTotal, formatFavoritePrice } from '../utils/favoritePrice'

const FavoritesModal = ({ isOpen, onClose }) => {
  const { favorites, toggleFavorite } = useFavorites()

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  if (!isOpen) return null

  const totals = favoritesTotal(favorites)

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-ink-soft border border-ivory/10 text-ivory rounded-2xl shadow-2xl shadow-black/60 max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-ivory/10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl border border-gold/30 bg-gold/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-gold fill-gold" />
            </div>
            <div>
              <h2 className="font-serif text-2xl font-medium text-ivory">Favorites</h2>
              <p className="text-sm text-ivory-muted">{favorites.length} items</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-ivory-faint hover:text-ivory hover:bg-ivory/5 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-0">
          {favorites.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="w-16 h-16 text-ivory/20 mx-auto mb-4" />
              <h3 className="font-serif text-xl font-medium text-ivory mb-2">No favorites yet</h3>
              <p className="text-ivory-muted">Start adding items to your favorites by clicking the heart icon on any product.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {favorites.map((item) => (
                <div key={item.id} className="flex items-start gap-3 sm:items-center sm:gap-4 p-3 sm:p-4 rounded-xl border border-ivory/10 bg-ink hover:border-ivory/20 transition-colors">
                  {/* Product Image */}
                  <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-full h-full object-cover rounded-lg"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=300&h=400&fit=crop&crop=center&bg=white'
                      }}
                    />
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-ivory line-clamp-2 sm:truncate">{item.name}</h3>
                    {item.brand && <p className="text-sm text-gold font-medium">{item.brand}</p>}
                    {[item.type, item.material, item.quality].some(Boolean) && (
                      <p className="text-sm text-ivory-muted mt-1">
                        {[item.type, item.material, item.quality].filter(Boolean).join(' • ')}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-2 mt-2">
                      {formatFavoritePrice(item.price) ? (
                        <span className="text-lg font-semibold text-ivory">{formatFavoritePrice(item.price)}</span>
                      ) : (
                        <span className="text-sm text-ivory-faint">Price not listed</span>
                      )}
                      {favoritePrice(item.originalPrice) > (favoritePrice(item.price) ?? Infinity) && (
                        <span className="text-sm text-ivory-faint line-through">{formatFavoritePrice(item.originalPrice)}</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => toggleFavorite(item)}
                      className="p-2 rounded-full border border-ivory/25 text-gold hover:border-ivory/50 hover:bg-ivory/5 transition-colors"
                      title="Remove from favorites"
                      aria-label="Remove from favorites"
                    >
                      <Heart className="w-4 h-4 fill-gold" />
                    </button>
                    {item.directLink && (
                      <a
                        href={item.directLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-gold text-ink rounded-full hover:bg-gold-light transition-colors"
                        title="View product"
                        aria-label="View product"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {favorites.length > 0 && (
          <div className="p-4 sm:p-6 border-t border-ivory/10 bg-ink rounded-b-2xl">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-col min-w-0">
                <span className="text-sm text-ivory-muted">
                  {favorites.length} item{favorites.length !== 1 ? 's' : ''} in favorites
                </span>
                {totals.priced > 0 && (
                  <span className="text-lg font-semibold text-ivory">
                    Total: ${totals.total.toFixed(2)}
                    {totals.priced < favorites.length && (
                      <span className="ml-1 text-sm font-normal text-ivory-faint">
                        ({favorites.length - totals.priced} without a listed price)
                      </span>
                    )}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FavoritesModal 